import { formatDuration, formatElapsed, formatWall, parseAbsoluteInstant } from './format'
import { displayDurationMs, plannedLengthMs } from './resolveSession'
import { paletteLabel } from './palette'
import { commentWho, type CommentWhoOpts } from './pageComments'
import { sessionCardSubtitle, typeOffersStoryClock } from './sessionTypes'
import {
  personIsActive,
  personUserId,
  resolveDesignatedOwnerUserId,
} from './pagePeopleModel'
import type { ScenarioComment, ScenarioInstance, ScenarioRun, ScenarioSession, ScenarioPerson } from './types'
import { sameLoginEmail } from './user'

/** Stored templates are shared state — always seed boards from a detached copy. */
export function cloneSession(session: ScenarioSession): ScenarioSession {
  return JSON.parse(JSON.stringify(session)) as ScenarioSession
}
/** Active roster row: invited or accepted. Declined and removed rows do not count. */
export function personOnRoster(
  people: ScenarioPerson[],
  email: string | null | undefined,
  userId?: string | null,
): boolean {
  const uid = userId?.trim() ?? ''
  if (uid) {
    const byId = people.some((p) => {
      if (p.isDeleted || p.status === 'declined') return false
      return personUserId(p) === uid
    })
    if (byId) return true
  }
  const key = email?.trim().toLowerCase() ?? ''
  if (!key) return false
  return people.some((p) => {
    if (p.isDeleted || p.status === 'declined') return false
    return sameLoginEmail(p.email, email)
  })
}

/** Newest first by ISO timestamp (invalid/missing sorts last). */
export function sortIsoDesc(a: string | undefined, b: string | undefined): number {
  const ta = a ? parseAbsoluteInstant(a).getTime() : NaN
  const tb = b ? parseAbsoluteInstant(b).getTime() : NaN
  const na = Number.isFinite(ta) ? ta : 0
  const nb = Number.isFinite(tb) ? tb : 0
  return nb - na
}

/** Soonest schedule first; unscheduled after scheduled; then newest created. */
export function sortPlannedBySchedule(a: ScenarioInstance, b: ScenarioInstance): number {
  const aS = a.session.scheduledStartTime
    ? parseAbsoluteInstant(a.session.scheduledStartTime).getTime()
    : NaN
  const bS = b.session.scheduledStartTime
    ? parseAbsoluteInstant(b.session.scheduledStartTime).getTime()
    : NaN
  const aOk = Number.isFinite(aS)
  const bOk = Number.isFinite(bS)
  if (aOk && bOk && aS !== bS) return aS - bS
  if (aOk && !bOk) return -1
  if (!aOk && bOk) return 1
  return sortIsoDesc(a.createdAtIso, b.createdAtIso)
}

/** Oversight / activity lists: Live → Planned → Done; within each, newest activity first. */
export function sortOversightRail(a: ScenarioInstance, b: ScenarioInstance): number {
  const rank = (lifecycle: string): number =>
    lifecycle === 'running' ? 0 : lifecycle === 'planned' ? 1 : 2
  const d = rank(a.lifecycle) - rank(b.lifecycle)
  if (d !== 0) return d
  if (a.lifecycle === 'running') {
    return sortIsoDesc(a.actualStartTime ?? a.createdAtIso, b.actualStartTime ?? b.createdAtIso)
  }
  if (a.lifecycle === 'completed') {
    return sortIsoDesc(a.actualEndTime ?? a.createdAtIso, b.actualEndTime ?? b.createdAtIso)
  }
  return sortIsoDesc(a.createdAtIso, b.createdAtIso)
}

/**
 * Card duration only when story-clock boards have an authored end.
 * Empty string = untimed / time doesn't matter — caller omits it from the subtitle.
 */
export function boardDurationLabel(board: ScenarioSession): string {
  if (!typeOffersStoryClock(board.sessionType)) return ''
  const planned = plannedLengthMs(board)
  if (planned != null && planned > 0) return formatDuration(planned)
  return ''
}

/** Scheduled meeting span when both schedule ends are set. */
export function scheduleDurationLabel(board: ScenarioSession): string {
  const start = board.scheduledStartTime ? Date.parse(board.scheduledStartTime) : NaN
  const end = board.scheduledEndTime ? Date.parse(board.scheduledEndTime) : NaN
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return ''
  return formatDuration(end - start)
}

function formatWallTime(iso: string): string {
  const d = parseAbsoluteInstant(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, { hour: 'numeric', minute: '2-digit' })
}

function sameLocalDay(a: string, b: string): boolean {
  const da = parseAbsoluteInstant(a)
  const db = parseAbsoluteInstant(b)
  if (Number.isNaN(da.getTime()) || Number.isNaN(db.getTime())) return false
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  )
}

/**
 * Card lead for Schedule: start only, end only, or start–end window.
 * Same calendar day collapses to "Oct 4, 2:04–3:30 PM".
 */
export function formatScheduleCardLead(board: ScenarioSession): string | null {
  const start = board.scheduledStartTime
  const end = board.scheduledEndTime
  if (!start && !end) return null
  if (start && !end) return formatWall(start)
  if (!start && end) return `Until ${formatWall(end)}`
  if (sameLocalDay(start!, end!)) {
    const day = new Date(start!).toLocaleString(undefined, { month: 'short', day: 'numeric' })
    return `${day}, ${formatWallTime(start!)}–${formatWallTime(end!)}`
  }
  return `${formatWall(start!)} – ${formatWall(end!)}`
}

/**
 * Draft / Published / Invited / Joined cards: scheduled start–end when set.
 * Falls back to instance create time so plain boards still show *when*.
 */
export function plannedSessionCardSubtitle(
  board: ScenarioSession,
  createdAtIso?: string | null,
): string {
  const when =
    formatScheduleCardLead(board) ?? (createdAtIso ? formatWall(createdAtIso) : null)
  return sessionCardSubtitle('', board.sessionType, when)
}

/** Short display name for rail cards (strip leading role tokens like invite toasts). */
function cardPersonName(person: ScenarioPerson): string {
  const parts = person.name?.trim().split(/\s+/) ?? []
  if (parts.length > 1 && ['Owner', 'Trainer', 'Editor', 'Participant'].includes(parts[0]!)) {
    const rest = parts.slice(1).join(' ')
    if (rest && !/^\d+$/.test(rest)) return rest
  }
  return person.name?.trim() || 'Someone'
}

/**
 * Card line 2 byline — designated owner (always, including your own cards).
 * Answers “whose session is this?” without crowding the meta line.
 * Falls back to first active roster row when no owner seat is stamped yet (solo Drafts).
 */
export function sessionCardByline(
  board: ScenarioSession,
  people: readonly ScenarioPerson[],
  myUserId?: string | null,
): string | null {
  const ownerId = resolveDesignatedOwnerUserId(board.primaryOwnerUserId, people)
  const owner =
    (ownerId
      ? people.find((p) => personIsActive(p) && personUserId(p) === ownerId)
      : undefined) ??
    (myUserId?.trim()
      ? people.find((p) => personIsActive(p) && personUserId(p) === myUserId.trim())
      : undefined) ??
    people.find((p) => personIsActive(p))
  if (!owner) return null
  return `by ${cardPersonName(owner)}`
}

/**
 * Whose session / who invited — for Joined (and Live when you are not the host).
 * Prefer last inviter ("From …"); fall back to designated owner ("Hosted by …").
 */
export function sessionHostCardLead(
  board: ScenarioSession,
  people: readonly ScenarioPerson[],
  myUserId?: string | null,
): string | null {
  const uid = myUserId?.trim() ?? ''
  const me = uid
    ? people.find((p) => personIsActive(p) && personUserId(p) === uid)
    : undefined
  const inviterId = me?.lastInviterId?.trim()
  if (inviterId) {
    const inviter = people.find(
      (p) => personIsActive(p) && (personUserId(p) === inviterId || p.id === inviterId),
    )
    if (inviter) return `From ${cardPersonName(inviter)}`
  }
  const ownerId = resolveDesignatedOwnerUserId(board.primaryOwnerUserId, people)
  if (!ownerId || (uid && ownerId === uid)) return null
  const owner = people.find((p) => personIsActive(p) && personUserId(p) === ownerId)
  if (!owner) return null
  return `Hosted by ${cardPersonName(owner)}`
}

/** Joined rail meta (line 3): schedule/create · type — byline is separate. */
export function joinedSessionCardSubtitle(
  board: ScenarioSession,
  _people?: readonly ScenarioPerson[],
  _myUserId?: string | null,
  createdAtIso?: string | null,
): string {
  return plannedSessionCardSubtitle(board, createdAtIso)
}

/** Live actual-clock length of a completed run (started → completed). */
export function instanceActualDurationLabel(inst: ScenarioInstance): string | null {
  const a = inst.actualStartTime ? Date.parse(inst.actualStartTime) : NaN
  const b = inst.actualEndTime ? Date.parse(inst.actualEndTime) : NaN
  if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return null
  return formatDuration(b - a)
}

/** Done rail card: Go Live actual time · actual length · session type. */
export function doneSessionCardSubtitle(inst: ScenarioInstance, board?: ScenarioSession): string {
  const session = board ?? inst.session
  const when = inst.actualStartTime ? formatWall(inst.actualStartTime) : null
  const actual = instanceActualDurationLabel(inst)
  const plannedLabel = boardDurationLabel(session)
  const dur = actual ?? (plannedLabel || null)
  return sessionCardSubtitle(dur ?? '', session.sessionType, when ?? (!dur ? 'Done' : null))
}

/**
 * Live rail card: Go Live actual time · how long the room has been up.
 * Omit session type — the Live pill uses that width; Done cards include type.
 */
export function liveSessionCardSubtitle(
  inst: ScenarioInstance,
  _board?: ScenarioSession,
  goLiveIso?: string | null,
): string {
  const startIso = goLiveIso ?? inst.actualStartTime ?? null
  const when = startIso ? formatWall(startIso) : null
  const startMs = startIso ? Date.parse(startIso) : NaN
  const liveMs = Number.isFinite(startMs) ? Math.max(0, Date.now() - startMs) : null
  // Elapsed wall-up — whole minutes (`3 hrs 59 min` / `1 day 22 hrs …`), same shape as Done cards.
  const dur = liveMs != null ? formatDuration(liveMs) : null
  return [when, dur].filter(Boolean).join(' · ') || 'Live'
}

export function defaultLessons(session: ScenarioSession): string {
  const title = session.title.toLowerCase()
  if (title.includes('eagle mountain') || title.includes('kaiser')) {
    return 'Wind-driven fire growth at the school required holding Yucca until EMS staged in the lot. The Kaiser Road close held the lowboy at the work zone as designed. Live edits during the run are on the current board.'
  }
  const planned = plannedLengthMs(session)
  const shown = planned != null && planned > 0 ? planned : displayDurationMs(session)
  return `Timed injects ran to ${formatElapsed(shown)}. Live contributions from the team remain on the current board.`
}

export function instanceRun(inst: ScenarioInstance, people: readonly ScenarioPerson[]): ScenarioRun {
  const started = inst.actualStartTime ?? inst.createdAtIso
  const stillRunning = inst.lifecycle === 'running' && !inst.actualEndTime
  const completed = stillRunning ? '' : (inst.actualEndTime ?? new Date().toISOString())
  const startedMs = Date.parse(started)
  const completedMs = stillRunning ? Date.now() : Date.parse(completed)
  const actualDurationMs =
    Number.isFinite(startedMs) && Number.isFinite(completedMs) && completedMs >= startedMs
      ? completedMs - startedMs
      : displayDurationMs(inst.session)
  const planned = plannedLengthMs(inst.session) ?? 0
  const trainer =
    people.find((p) => !p.isDeleted && p.roles.includes('owner')) ??
    people.find((p) => !p.isDeleted && p.roles.includes('trainer')) ??
    people.find((p) => !p.isDeleted && p.status === 'joined') ??
    null
  const attendees = people
    .filter((p) => !p.isDeleted && p.status === 'joined')
    .map((p) => ({
      name: p.name.trim() || p.email || 'Participant',
      email: p.email.trim(),
      ...(p.phone?.trim() ? { phone: p.phone.trim() } : {}),
      ...(p.jobTitle?.trim() ? { jobTitle: p.jobTitle.trim() } : {}),
      roles: p.roles,
    }))
  const commentTopics: ScenarioRun['commentTopics'] = []
  const whoOpts: CommentWhoOpts = {
    session: inst.session,
    omitContactDetails: inst.session.omitContactDetailsFromExports,
  }
  const pushTopic = (title: string, comments: readonly ScenarioComment[]) => {
    const visible = comments.filter((c) => !c.removedAtWallIso)
    if (!visible.length) return
    const contributors = [
      ...new Set(visible.map((c) => commentWho(c, people, whoOpts))),
    ].sort((a, b) => a.localeCompare(b))
    commentTopics.push({ title, count: visible.length, contributors })
  }
  pushTopic('Scenario', inst.session.comments)
  for (const obj of inst.session.objects) {
    if (!obj.comments.length) continue
    pushTopic(obj.label.trim() || paletteLabel(obj.type), obj.comments)
  }
  return {
    id: inst.id,
    scenarioId: inst.id,
    title: inst.session.title,
    summary: inst.session.summary,
    scheduledStartTime: inst.session.scheduledStartTime,
    scheduledEndTime: inst.session.scheduledEndTime,
    actualStartTime: started,
    actualEndTime: completed,
    plannedDurationMs: planned,
    actualDurationMs,
    durationMs: actualDurationMs,
    trainerName: trainer?.name.trim() || '—',
    trainerEmail: trainer?.email.trim() || '',
    attendees,
    commentTopics,
    attendeeNames: attendees.map((a) => a.name),
    lessons: inst.lessons ?? defaultLessons(inst.session),
    omitContactDetailsFromExports: inst.session.omitContactDetailsFromExports,
    storyStartTime: inst.session.storyStartTime,
    storyEndTime: inst.session.storyEndTime,
    storyTimezone: inst.session.timezone,
    storyStartDateSet: inst.session.storyStartDateSet,
    storyEndDateSet: inst.session.storyEndDateSet,
    timelineTimeFormat: inst.session.timelineTimeFormat,
    showTimeZone: inst.session.showTimeZone,
    sessionType: inst.session.sessionType,
  }
}
